import type { AxiosResponse } from 'axios';
import mem from 'mem';
import type {
  ActivateTotpResponse,
  LoginResponse,
  SetupTotpResponse,
} from '@/types';
import request from '@/utils/request';

export const TOKEN_URL = '/api/v1/auth/token/';

export const getToken = async ({
  username,
  password,
  totpCode,
  recoveryCode,
}: {
  username: string;
  password: string;
  totpCode?: string;
  recoveryCode?: string;
}): Promise<LoginResponse> => {
  const url = TOKEN_URL;
  const data = {
    username: username.toLowerCase(),
    password,
    totpCode,
    recoveryCode,
  };

  return (await request.post(url, data)).data as LoginResponse;
};

export const LOGOUT_URL = '/api/v1/auth/logout/';

export const logout = async (): Promise<void> => {
  await request.post(LOGOUT_URL);
};

export const memLogout = mem(logout);

export const PASSWORD_RESET_URL = '/api/v1/auth/password/reset/';

export const requestPasswordReset = async (email: string): Promise<void> => {
  const data = {
    email,
  };

  await request.post(PASSWORD_RESET_URL, data);
};

export const PASSWORD_RESET_CONFIRM_URL =
  '/api/v1/auth/password/reset/confirm/';

export const confirmPasswordReset = async ({
  uid,
  token,
  newPassword,
}: {
  uid: string;
  token: string;
  newPassword: string;
}): Promise<void> => {
  const data = {
    uid,
    token,
    newPassword,
  };

  await request.post(PASSWORD_RESET_CONFIRM_URL, data);
};

export const PASSWORD_CHANGE_URL = '/api/v1/auth/password/change/';

export const passwordChange = async (
  oldPassword: string,
  newPassword: string,
): Promise<void> => {
  await request.put(PASSWORD_CHANGE_URL, { oldPassword, newPassword });
};

export const TOTP_SETUP_URL = '/api/v1/auth/totp/setup/';

export const setupTotp = async (): Promise<
  AxiosResponse<SetupTotpResponse>
> => {
  return request.post(TOTP_SETUP_URL);
};

export const TOTP_ACTIVATE_URL = '/api/v1/auth/totp/activate/';

export const activateTotp = async (
  code: string,
): Promise<ActivateTotpResponse> => {
  return (await request.post(TOTP_ACTIVATE_URL, { code })).data;
};

export const TOTP_DISABLE_URL = '/api/v1/auth/totp/disable/';

export const disableTotp = async (): Promise<void> => {
  return request.post(TOTP_DISABLE_URL);
};

export const TOTP_VERIFY_URL = '/api/v1/auth/totp/verify/';

export const verifyTotp = async (
  scope: string,
  code?: string,
  recoveryCode?: string,
): Promise<void> => {
  return request.post(TOTP_VERIFY_URL, { scope, code, recoveryCode });
};
